/**
 * A sketch of a dashboard's layout from the kinds of chart it holds.
 *
 * Drawn from the summary alone, so a list of eighty dashboards paints without
 * fetching eighty sets of rows. It says what kind of dashboard this is at a
 * glance; it does not pretend to be the data.
 */
import { chartLabel } from "./workspaceView";

const MAX_TILES = 4;

export function ChartSketch({ chartType }: { chartType: string }) {
  switch (chartType) {
    case "line":
      return (
        <svg viewBox="0 0 60 32" aria-hidden="true">
          <polyline points="2,26 14,18 24,21 36,9 46,13 58,4" />
        </svg>
      );
    case "pie":
      return (
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <circle className="track" cx="16" cy="16" r="10" />
          <circle className="arc" cx="16" cy="16" r="10" pathLength="100" strokeDasharray="62 100" />
        </svg>
      );
    case "scalar":
      return (
        <svg viewBox="0 0 60 32" aria-hidden="true">
          <rect className="solid" x="10" y="9" width="30" height="10" rx="2" />
          <rect className="faint" x="10" y="23" width="40" height="4" rx="2" />
        </svg>
      );
    case "table":
      return (
        <svg viewBox="0 0 60 32" aria-hidden="true">
          {[5, 13, 21, 29].map((y, index) => (
            <rect key={y} className={index === 0 ? "solid" : "faint"} x="4" y={y - 2} width="52" height="4" rx="1.5" />
          ))}
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 60 32" aria-hidden="true">
          {[14, 22, 10, 26, 18].map((height, index) => (
            <rect key={index} className="solid" x={5 + index * 11} y={30 - height} width="7" height={height} rx="1.5" />
          ))}
        </svg>
      );
  }
}

export function DashboardPreview({
  chartTypes,
  panelCount,
}: {
  chartTypes: string[];
  panelCount: number;
}) {
  const kinds = chartTypes.length > 0 ? chartTypes : ["bar"];
  const count = Math.max(1, Math.min(MAX_TILES, panelCount || kinds.length));
  const tiles = Array.from({ length: count }, (_, index) => kinds[index % kinds.length] ?? "bar");
  const label = `Holds ${chartTypes.map(chartLabel).join(", ") || "charts"}`;
  return (
    <div className={`dashboard-sketch tiles-${count}`} role="img" aria-label={label}>
      {tiles.map((chartType, index) => (
        <span key={index} className={`sketch-tile ${chartType}`}>
          <ChartSketch chartType={chartType} />
        </span>
      ))}
    </div>
  );
}
